import { Fragment } from 'react';
import { Button, Radio } from 'antd';

import type { AxisOptionGroup } from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import { colorConstants } from 'config/colorConstants';
import {
    PPA_AXIS_CONFIG_APPLY_BTN,
    PPA_AXIS_CONFIG_PANEL,
    ppaAxisConfigOptionTestId,
} from 'constants/ppaTestIds';

type AxisConfigDropdownPanelProps = {
    optionGroups: AxisOptionGroup[];
    draftValue: string;
    onDraftChange: (value: string) => void;
    onApply: () => void;
};

const PANEL_WIDTH: Record<number, string> = {
    1: 'w-[280px]',
    2: 'w-[400px]',
    3: 'w-[568px]',
    4: 'w-[736px]',
};

const AxisConfigDropdownPanel = ({
    optionGroups,
    draftValue,
    onDraftChange,
    onApply,
}: AxisConfigDropdownPanelProps) => {
    const colCount = Math.min(optionGroups.length, 4) as 1 | 2 | 3 | 4;
    const panelWidth = PANEL_WIDTH[colCount] ?? 'w-[568px]';

    return (
        <div
            className={`${panelWidth} flex flex-col overflow-hidden rounded-lg border border-borderSecondary bg-white px-6 py-4 shadow-axesDropdown`}
            data-testid={PPA_AXIS_CONFIG_PANEL}
        >
            <div className="flex w-full">
                {optionGroups.map((group, index) => (
                    <Fragment key={group.title}>
                        {index > 0 ? (
                            <div
                                className="ml-2 mr-7 flex shrink-0 self-stretch py-2"
                                aria-hidden
                            >
                                <div
                                    className="w-px self-stretch"
                                    style={{ backgroundColor: colorConstants.tableColorSplit }}
                                />
                            </div>
                        ) : null}
                        <div className="flex min-w-0 flex-1 flex-col gap-3">
                            {colCount > 1 ? (
                                <p className="m-0 text-sm font-semibold leading-[22px] text-axisGroupTitle">
                                    {group.title}
                                </p>
                            ) : null}
                            <Radio.Group
                                value={draftValue}
                                onChange={(event) => onDraftChange(event.target.value)}
                                className="!flex !flex-col !gap-[8px] [&>.ant-radio-wrapper]:!m-0 [&>.ant-radio-wrapper]:!text-sm [&>.ant-radio-wrapper]:!leading-[24px] [&>.ant-radio-wrapper]:!text-blackAlt [&>.ant-radio-wrapper>span:last-child]:!ml-3 [&>.ant-radio-wrapper>span:last-child]:!rounded-[6px] [&>.ant-radio-wrapper>span:last-child]:!px-1 [&>.ant-radio-wrapper>span:last-child]:!py-0"
                            >
                                {group.options.map((option) => (
                                    <Radio
                                        key={option.value}
                                        value={option.value}
                                        data-testid={ppaAxisConfigOptionTestId(option.value)}
                                    >
                                        {option.label}
                                    </Radio>
                                ))}
                            </Radio.Group>
                        </div>
                    </Fragment>
                ))}
            </div>
            <div className="my-4 w-full border-t border-backgroundGreyAlt1" />
            <div className="flex w-full justify-end">
                <Button
                    type="primary"
                    onClick={onApply}
                    className="axis-dropdown-apply-btn !h-auto !rounded-[6px] !px-4 !py-[5px] !text-sm !font-medium"
                    data-testid={PPA_AXIS_CONFIG_APPLY_BTN}
                >
                    Apply
                </Button>
            </div>
        </div>
    );
};

export default AxisConfigDropdownPanel;
